import React, { useEffect, useRef, useState } from "react";
import { Heart, Volume2, VolumeX, Clapperboard, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const ytIdOf = (src) => (src?.match(/(?:v=|embed\/|youtu\.be\/|shorts\/)([\w-]{6,})/) || [])[1];
const resolve = (u) => (u?.startsWith("/api/") ? `${API}${u}` : u);

function ReelSlide({ reel, rootRef, onToggle }) {
  const yt = ytIdOf(reel.src);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.intersectionRatio >= 0.6) v.play().catch(() => {});
        else v.pause();
      },
      { root: rootRef.current, threshold: [0, 0.6] }
    );
    obs.observe(v);
    return () => obs.disconnect();
  }, [rootRef]);

  return (
    <div className="relative h-full w-full snap-start snap-always bg-black" data-testid={`reel-slide-${reel.id}`}>
      {yt ? (
        <iframe src={`https://www.youtube.com/embed/${yt}?rel=0&loop=1&playlist=${yt}`} title={reel.title || "Reel"}
                loading="lazy" className="w-full h-full" frameBorder="0" allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
      ) : (
        <video ref={videoRef} src={resolve(reel.src)} loop muted={muted} playsInline preload="metadata"
               controlsList="nodownload" disablePictureInPicture onContextMenu={e => e.preventDefault()}
               className="w-full h-full object-contain cursor-pointer" onClick={() => setMuted((m) => !m)} />
      )}

      <div className="absolute right-3 bottom-20 flex flex-col items-center gap-4">
        <div className="flex flex-col items-center gap-1">
          <button onClick={() => onToggle(reel.id)} data-testid={`reel-like-${reel.id}`} aria-label="Like reel"
                  className="w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-md transition-transform active:scale-90"
                  style={{ background: "rgba(0,0,0,0.4)" }}>
            <Heart size={20} className={reel.liked ? "text-red-500" : "text-white"} fill={reel.liked ? "currentColor" : "none"} />
          </button>
          <span className="text-white text-xs font-semibold drop-shadow" data-testid={`reel-likes-${reel.id}`}>{reel.likes || 0}</span>
        </div>
        {!yt && (
          <button onClick={() => setMuted((m) => !m)} data-testid={`reel-mute-${reel.id}`} aria-label="Toggle sound"
                  className="w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-md"
                  style={{ background: "rgba(0,0,0,0.4)" }}>
            {muted ? <VolumeX size={18} className="text-white" /> : <Volume2 size={18} className="text-white" />}
          </button>
        )}
      </div>

      {reel.title && (
        <div className="absolute inset-x-0 bottom-0 px-4 pt-14 pb-5 pointer-events-none"
             style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.7))" }}>
          <p className="text-white text-sm font-semibold pr-14">{reel.title}</p>
        </div>
      )}
    </div>
  );
}

export default function ReelsFeed({ reels: initial }) {
  const [reels, setReels] = useState(initial || []);
  const rootRef = useRef(null);
  useEffect(() => setReels(initial || []), [initial]);

  const toggle = async (id) => {
    setReels((rs) => rs.map((r) => (r.id === id ? { ...r, liked: !r.liked, likes: (r.likes || 0) + (r.liked ? -1 : 1) } : r)));
    try {
      const res = await fetch(`${API}/api/tribe/reels/${id}/like`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setReels((rs) => rs.map((r) => (r.id === id ? { ...r, liked: d.liked, likes: d.likes } : r)));
      }
    } catch {}
  };

  if (!reels.length)
    return (
      <div className="rp-foam bg-white/45 p-10 text-center" data-testid="tribe-reels-empty">
        <Clapperboard className="mx-auto mb-4 text-[#B5793F]" size={24} strokeWidth={1.5} />
        <p className="rp-display rp-italic text-base md:text-lg text-[#8A7E6B]">
          Reels are on their way — short, sharp creative drops will land here.
        </p>
      </div>
    );

  return (
    <div className="flex flex-col items-center" data-testid="tribe-reels-feed">
      <div ref={rootRef} className="snap-y snap-mandatory overflow-y-auto rounded-3xl bg-black shadow-2xl"
           style={{ height: "min(calc(100dvh - 230px), 740px)", width: "min(100%, 420px)", scrollbarWidth: "none" }}>
        {reels.map((r) => (
          <ReelSlide key={r.id} reel={r} rootRef={rootRef} onToggle={toggle} />
        ))}
      </div>
      {reels.length > 1 && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-[#8A7E6B]">
          <ChevronDown size={13} /> Scroll for more
        </p>
      )}
    </div>
  );
}
